<template>
    <b-card v-if="widget" class="ranzcr-dashboard-widget mb-4">
        <div class="ranzcr-dashboard-widget__header">
            <h2 class="ranzcr-dashboard-widget__title">{{ widget.valid ? widget.data.title : 'RANZCR' }}</h2>
            <span v-if="widget.valid" class="ranzcr-dashboard-widget__verdict" :class="outstandingItems === 0 ? 'ranzcr-dashboard-widget__verdict--complete' : 'ranzcr-dashboard-widget__verdict--attention'">
                {{ outstandingItems === 0 ? 'Complete' : `Attention needed · ${outstandingItems} items` }}
            </span>
        </div>

        <p v-if="!widget.valid" class="mb-0">
            Please configure your RANZCR Specialty and Registration in your
            <router-link :to="{ name: 'profile' }">Profile</router-link> to set up this widget.
        </p>
        <div v-else class="ranzcr-hours">
            <div class="ranzcr-hours__card">
                <div class="ranzcr-hours__label">Total Hours</div>
                <div class="ranzcr-hours__headline">
                    <strong>{{ hours.total.value.toFixed(1) }}</strong>
                    <span> / {{ hours.total.target }}h</span>
                </div>
                <b-progress :value="Math.min(hours.total.value, hours.total.target)" :max="hours.total.target" :variant="hours.total.value < hours.total.target ? 'danger' : 'success'" height="4px" />
            </div>

            <div class="ranzcr-hours__card">
                <div class="ranzcr-hours__label">Educational</div>
                <div class="ranzcr-hours__headline">
                    <strong>{{ hours.educational.value.toFixed(1) }}</strong>
                    <span> / {{ hours.educational.minimum }}h min</span>
                </div>
                <b-progress :value="Math.min(hours.educational.value, hours.educational.minimum)" :max="hours.educational.minimum" :variant="hours.educational.value < hours.educational.minimum ? 'danger' : 'success'" height="4px" />
                <div class="ranzcr-hours__status" :class="hours.educational.value < hours.educational.minimum ? 'text-danger' : 'text-success'">{{ minimumStatus(hours.educational) }}</div>
            </div>

            <div class="ranzcr-hours__card">
                <div class="ranzcr-hours__label">Reviewing Performance</div>
                <div class="ranzcr-hours__headline">
                    <strong>{{ hours.reviewing_performance.value.toFixed(1) }}</strong>
                    <span> / {{ hours.reviewing_performance.minimum }}h min</span>
                </div>
                <b-progress :value="Math.min(hours.reviewing_performance.value, hours.reviewing_performance.minimum)" :max="hours.reviewing_performance.minimum" :variant="hours.reviewing_performance.value < hours.reviewing_performance.minimum ? 'danger' : 'success'" height="4px" />
                <div class="ranzcr-hours__status" :class="hours.reviewing_performance.value < hours.reviewing_performance.minimum ? 'text-danger' : 'text-success'">{{ minimumStatus(hours.reviewing_performance) }}</div>
            </div>

            <div class="ranzcr-hours__card">
                <div class="ranzcr-hours__label">Measuring Outcomes</div>
                <div class="ranzcr-hours__headline">
                    <strong>{{ hours.measuring_outcomes.value.toFixed(1) }}</strong>
                    <span> / {{ hours.measuring_outcomes.minimum }}h min</span>
                </div>
                <b-progress :value="Math.min(hours.measuring_outcomes.value, hours.measuring_outcomes.minimum)" :max="hours.measuring_outcomes.minimum" :variant="hours.measuring_outcomes.value < hours.measuring_outcomes.minimum ? 'danger' : 'success'" height="4px" />
                <div class="ranzcr-hours__status" :class="hours.measuring_outcomes.value < hours.measuring_outcomes.minimum ? 'text-danger' : 'text-success'">{{ minimumStatus(hours.measuring_outcomes) }}</div>
            </div>

            <div class="ranzcr-hours__card ranzcr-hours__card--combined">
                <div class="ranzcr-hours__label">Reviewing + Measuring</div>
                <div class="ranzcr-hours__headline">
                    <strong>{{ hours.reviewing_measuring.value.toFixed(1) }}</strong>
                    <span> / {{ hours.reviewing_measuring.target }}h{{ hours.reviewing_measuring.targetEditable ? ' target' : ' min' }}</span>
                </div>
                <b-progress :value="Math.min(hours.reviewing_measuring.value, hours.reviewing_measuring.target)" :max="hours.reviewing_measuring.target" :variant="hours.reviewing_measuring.value < hours.reviewing_measuring.minimum ? 'danger' : 'success'" height="4px" />
                <div class="ranzcr-hours__status" :class="hours.reviewing_measuring.value < hours.reviewing_measuring.minimum ? 'text-danger' : 'text-success'">{{ combinedStatus }}</div>
                <div class="ranzcr-hours__caption">
                    + {{ flexibleHours }}h from any category reaches {{ hours.total.target }}h
                </div>
            </div>
        </div>
        <div v-if="widget.valid && weightingBanner" class="ranzcr-dashboard-widget__banner">{{ weightingBanner }}</div>
        <div v-if="widget.valid" class="ranzcr-checklists">
            <section>
                <h3>Program Level Requirements</h3>
                <ul>
                    <li>
                        <span class="font-weight-bold" :class="plrs.professionalism ? 'text-success' : 'text-danger'">{{ plrs.professionalism ? '✓' : '◯' }}</span>
                        Professionalism
                        <div v-if="plrs.professionalism" class="ranzcr-checklists__evidence text-muted">
                            <span v-if="plrs.professionalism.activity_type">{{ plrs.professionalism.activity_type }} · </span>{{ formatDate(plrs.professionalism.start_date) }}
                        </div>
                    </li>
                    <li>
                        <span class="font-weight-bold" :class="plrs.cultural_safety_practice ? 'text-success' : 'text-danger'">{{ plrs.cultural_safety_practice ? '✓' : '◯' }}</span>
                        Cultural Safety Practice
                        <span v-if="isMcnz" class="ranzcr-checklists__annotation">(also meets MCNZ cultural safety)</span>
                        <div v-if="plrs.cultural_safety_practice" class="ranzcr-checklists__evidence text-muted">
                            <span v-if="plrs.cultural_safety_practice.activity_type">{{ plrs.cultural_safety_practice.activity_type }} · </span>{{ formatDate(plrs.cultural_safety_practice.start_date) }}
                        </div>
                    </li>
                    <li>
                        <span class="font-weight-bold" :class="plrs.addressing_health_inequities ? 'text-success' : 'text-danger'">{{ plrs.addressing_health_inequities ? '✓' : '◯' }}</span>
                        Addressing Health Inequities
                        <span v-if="isMcnz" class="ranzcr-checklists__annotation">(also meets MCNZ health equity)</span>
                        <div v-if="plrs.addressing_health_inequities" class="ranzcr-checklists__evidence text-muted">
                            <span v-if="plrs.addressing_health_inequities.activity_type">{{ plrs.addressing_health_inequities.activity_type }} · </span>{{ formatDate(plrs.addressing_health_inequities.start_date) }}
                        </div>
                    </li>
                    <li>
                        <span class="font-weight-bold" :class="plrs.ethical_practice ? 'text-success' : 'text-danger'">{{ plrs.ethical_practice ? '✓' : '◯' }}</span>
                        Ethical Practice
                        <div v-if="plrs.ethical_practice" class="ranzcr-checklists__evidence text-muted">
                            <span v-if="plrs.ethical_practice.activity_type">{{ plrs.ethical_practice.activity_type }} · </span>{{ formatDate(plrs.ethical_practice.start_date) }}
                        </div>
                    </li>
                </ul>
            </section>
            <section>
                <h3>Annual requirements</h3>
                <ul>
                    <li>
                        <span class="font-weight-bold" :class="annual.pdp ? 'text-success' : 'text-danger'">{{ annual.pdp ? '✓' : '◯' }}</span>
                        PDP {{ currentYear }} ·
                        <span v-if="annual.pdp">completed {{ formatDate(annual.pdp) }}</span>
                        <router-link v-else :to="{ name: 'create/diary' }">not yet created</router-link>
                    </li>
                    <li v-if="annual.anaphylaxis_training">
                        <span class="font-weight-bold" :class="anaphylaxisClass">{{ annual.anaphylaxis_training.completed ? '✓' : (anaphylaxisPending ? '◐' : '◯') }}</span>
                        Anaphylaxis training ·
                        <span v-if="annual.anaphylaxis_training.completed">completed {{ formatDate(annual.anaphylaxis_training.completed) }}</span>
                        <span v-else>due by {{ formatDueDate(annual.anaphylaxis_training.due) }}</span>
                    </li>
                    <li v-if="isMcnz">
                        <span class="font-weight-bold" :class="annual.structured_annual_conversation.completed ? 'text-success' : 'text-danger'">{{ annual.structured_annual_conversation.completed ? '✓' : '◯' }}</span>
                        Structured Annual Conversation ·
                        <span v-if="annual.structured_annual_conversation.completed">completed {{ formatDate(annual.structured_annual_conversation.start_date) }} · {{ annual.structured_annual_conversation.hours.toFixed(1) }}h recorded</span>
                        <router-link v-else :to="{ name: 'create/diary', query: { ranzcrConversation: '1' } }">record it</router-link>
                    </li>
                </ul>
            </section>
        </div>
    </b-card>
</template>

<script>
import moment from 'moment';

export default {
    name: 'RanzcrDashboardWidget',
    data() {
        return {
            widget: null,
        };
    },
    computed: {
        weightingBanner() {
            if (!this.isMcnz) return null;
            return this.widget.data.specialty === 'Clinical Radiologist'
                ? 'From 2026 the MCNZ expects you to weight more of your 50 hours toward these two categories, taking hours from the flexible remainder. You set your own distribution.'
                : 'Your 27h combined target already reflects the MCNZ increased emphasis on these two categories from 2026.';
        },
        outstandingItems() {
            const hours = this.hours;
            let count = [hours.educational, hours.reviewing_performance, hours.measuring_outcomes, hours.reviewing_measuring]
                .filter(card => card.value < card.minimum).length;
            count += Object.values(this.plrs).filter(requirement => requirement === false).length;
            
            if (!this.annual.pdp) count++;
            if (this.isMcnz && !this.annual.structured_annual_conversation.completed) count++;
            if (this.annual.anaphylaxis_training && !this.annual.anaphylaxis_training.completed && !this.anaphylaxisPending) count++;
            
            return count;
        },
        plrs() {
            return this.widget.data.checklists.program_level_requirements;
        },
        annual() {
            return this.widget.data.checklists.annual_requirements;
        },
        isMcnz() {
            return this.widget.data.registration === 'New Zealand';
        },
        currentYear() {
            return moment().year();
        },
        anaphylaxisPending() {
            return this.annual.anaphylaxis_training && moment(this.annual.anaphylaxis_training.due).year() > this.currentYear;
        },
        anaphylaxisClass() {
            if (this.annual.anaphylaxis_training.completed) return 'text-success';
            return this.anaphylaxisPending ? 'ranzcr-checklists__pending text-warning' : 'text-danger';
        },
        hours() {
            return this.widget.data.hours;
        },
        combinedStatus() {
            const card = this.hours.reviewing_measuring;
            if (card.targetEditable && card.value >= card.minimum && card.value < card.target) {
                return `Floor met · target ${card.target}h`;
            }
            return this.minimumStatus(card);
        },
        flexibleHours() {
            const hours = this.widget.data.hours;
            return Number((hours.total.target - hours.educational.minimum - hours.reviewing_measuring.target).toFixed(1));
        },
    },
    methods: {
        formatDate(date) {
            return moment(date, 'YYYY-MM-DD').format('D MMM YYYY');
        },
        formatDueDate(date) {
            return moment(date, 'YYYY-MM-DD').format('MMM YYYY');
        },
        minimumStatus(card) {
            return card.value < card.minimum
                ? `${Number((card.minimum - card.value).toFixed(1))}h below minimum`
                : 'Minimum met';
        },
    },
    created() {
        this.$api.v2.get('statistics/ranzcr').then(response => {
            this.widget = response.body;
        }).catch(error => {
            this.$httpError('Failed to load RANZCR dashboard', error);
        });
    },
};
</script>

<style scoped>
.ranzcr-dashboard-widget__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}

.ranzcr-dashboard-widget__verdict {
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 11px;
    white-space: nowrap;
}

.ranzcr-dashboard-widget__verdict--attention,
.ranzcr-dashboard-widget__banner {
    background: #faebd3;
    color: #66400c;
}

.ranzcr-dashboard-widget__verdict--complete {
    background: #e5f4eb;
    color: #17643d;
}

.ranzcr-dashboard-widget__banner {
    margin-top: 8px;
    border-radius: 6px;
    padding: 7px 10px;
    font-size: 11px;
}

.ranzcr-checklists__annotation {
    color: #999;
}

.ranzcr-checklists {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
    margin-top: 18px;
    font-size: 12px;
}

.ranzcr-checklists h3 {
    font-size: 12px;
    font-weight: 600;
    margin: 0 0 6px;
}

.ranzcr-checklists ul {
    list-style: none;
    margin: 0;
    padding: 0;
}

.ranzcr-checklists li {
    margin-bottom: 5px;
}

.ranzcr-checklists__evidence {
    margin-left: 14px;
    font-size: 11px;
}

.ranzcr-checklists__pending {
    font-size: calc(1em + 1px);
}

.ranzcr-dashboard-widget__title {
    margin: 0 0 6px;
    font-size: 18px;
    font-weight: 600;
}

.ranzcr-hours {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 4px;
    margin-top: 12px;
}

.ranzcr-hours__card {
    padding: 10px;
    border: 1px solid transparent;
    border-radius: 8px;
}

.ranzcr-hours__card--combined {
    border-color: #999;
}

.ranzcr-hours__label {
    font-size: 12px;
}

.ranzcr-hours__headline {
    margin: 3px 0 5px;
}

.ranzcr-hours__headline strong {
    font-size: 18px;
}

.ranzcr-hours__headline span,
.ranzcr-hours__caption {
    color: #999;
    font-size: 10px;
}

.ranzcr-hours__status {
    margin-top: 5px;
    font-size: 10px;
}

.ranzcr-hours__caption {
    margin-top: 3px;
}

@media (max-width: 575px) {
    .ranzcr-checklists {
        grid-template-columns: 1fr;
    }
    .ranzcr-hours {
        grid-template-columns: repeat(5, minmax(120px, 1fr));
        overflow-x: auto;
    }
}
</style>
